import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { getDomainInfo, getTeamDomainStatus } from '@/api/enterprise'
import { RootState } from '@/store'
import { getTenantRequestGeneration } from '@/store/tenantRequestFence'
import { Member, TeamDomainStatus } from '@/types'

interface DomainState {
  teamDomainStatus: TeamDomainStatus | null
  domainUsers: Member[] | null
  isLoading: boolean
  teamName: string | null
  teamMembers: Member[] | null
  subscriptionStatus: string | null
}

const initialState: DomainState = {
  teamDomainStatus: null,
  domainUsers: null,
  isLoading: false,
  teamName: null,
  teamMembers: null,
  subscriptionStatus: null,
}

export const fetchTeamDomainStatusThunk = createAsyncThunk(
  'domain/fetchTeamDomainStatus',
  async (_, { rejectWithValue }) => {
    const requestGeneration = getTenantRequestGeneration()
    const isStale = () => requestGeneration !== getTenantRequestGeneration()
    try {
      console.log('fetching team domain status')
      const { data } = await getTeamDomainStatus()
      if (isStale()) return rejectWithValue('Authentication changed')
      const teamDomainStatus = data.team_domain_status
      let domainInfo: Awaited<ReturnType<typeof getDomainInfo>>['data'] | null = null

      // If domain status is not forbidden, also fetch domain users
      if (teamDomainStatus && teamDomainStatus !== TeamDomainStatus.Forbidden) {
        try {
          domainInfo = (await getDomainInfo()).data
        } catch (domainUsersError) {
          if (isStale()) return rejectWithValue('Authentication changed')
          console.error('Failed to fetch domain users:', domainUsersError)
          // Don't fail the whole operation if domain users fetch fails
        }
      }

      if (isStale()) return rejectWithValue('Authentication changed')
      return { teamDomainStatus, domainInfo }
    } catch (error) {
      if (isStale()) return rejectWithValue('Authentication changed')
      const message = 'Failed to get team domain status'
      console.error(message, error)
      return rejectWithValue(message)
    }
  }
)

const domainSlice = createSlice({
  name: 'domain',
  initialState,
  reducers: {
    clearDomain: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTeamDomainStatusThunk.fulfilled, (state, action) => {
        state.isLoading = false
        state.teamDomainStatus = action.payload.teamDomainStatus
        if (action.payload.domainInfo) {
          state.domainUsers = action.payload.domainInfo.same_domain_users
          state.teamMembers = action.payload.domainInfo.team_members ?? null
          state.teamName = action.payload.domainInfo.team_name ?? null
          state.subscriptionStatus = action.payload.domainInfo.subscription_status ?? null
        }
      })
      .addCase(fetchTeamDomainStatusThunk.pending, (state) => {
        state.isLoading = true
      })
      .addCase(fetchTeamDomainStatusThunk.rejected, (state) => {
        state.isLoading = false
      })
  },
})

export const { clearDomain } = domainSlice.actions

// Selectors
export const selectTeamDomainStatus = (state: RootState) => state.domain.teamDomainStatus
export const selectIsDomainLoading = (state: RootState) => state.domain.isLoading
export const selectDomainUsers = (state: RootState) => state.domain.domainUsers

export const selectTeamName = (state: RootState) => state.domain.teamName
export const selectTeamMembers = (state: RootState) => state.domain.teamMembers
export const selectSubscriptionStatus = (state: RootState) => state.domain.subscriptionStatus

export default domainSlice.reducer
